import { Injectable } from '@angular/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { Hotspot, HotspotButton, HotspotInteraction } from '../model/hotspot-models';

@Injectable({
  providedIn: 'root'
})
export class HotspotService {
  private hotspotsEnabled: boolean;

  // The whole feature gets toggled on or off
  private hotspotsEnabledChange: Subject<boolean> = new ReplaySubject<boolean>(1);
  private hotspotEnabledChange: Subject<HotspotInteraction> = new Subject<HotspotInteraction>();
  private hotspotDetailEnabledChange: Subject<HotspotInteraction> = new Subject<HotspotInteraction>();
  private hotspotButtonClick: Subject<{ hotspot: Hotspot; button: HotspotButton }> = new Subject<{
    hotspot: Hotspot;
    button: HotspotButton;
  }>();

  constructor() {
    this.hotspotsEnabled = false;
  }

  /**
   * The entire feature is toggled on or off
   *
   * @param cb boolean signalising on or off
   */
  public onHotspotsEnabledChange(cb: (enabled: boolean) => void): Subscription {
    return this.hotspotsEnabledChange.subscribe(cb);
  }

  /**
   * A single hotspot gets activated (shown)
   *
   * @param cb the id of the hotspot
   */
  public onHotspotEnabledChange(cb: (hotspotID: HotspotInteraction) => void): Subscription {
    return this.hotspotEnabledChange.subscribe(cb);
  }

  /**
   * A single hotspot gets opened
   *
   * @param cb the id of the hotspot
   */
  public onHotspotDetailEnabledChange(cb: (hotspotID: HotspotInteraction) => void): Subscription {
    return this.hotspotDetailEnabledChange.subscribe(cb);
  }

  /** A click on one of the hotspots was registered */
  public onHotspotButtonClick(cb: (info: { hotspot: Hotspot; button: HotspotButton }) => void): Subscription {
    return this.hotspotButtonClick.subscribe(cb);
  }

  /**
   * An action within an hotspot gets gliched
   *
   * @param hotspot
   * @param button
   */
  public hotspotButtonClicked(hotspot: Hotspot, button: HotspotButton) {
    this.hotspotButtonClick.next({
      hotspot,
      button
    });
  }

  public isHotspotsEnabled(): boolean {
    return this.hotspotsEnabled;
  }

  public activateHotspot(hotspotID: string): void {
    this.hotspotEnabledChange.next({
      hotspotID,
      open: true
    });
  }

  public deactivateHotspot(hotspotID: string): void {
    this.hotspotEnabledChange.next({
      hotspotID,
      open: false
    });
  }

  public openHotspotDetail(hotspotID: string): void {
    this.hotspotDetailEnabledChange.next({
      hotspotID,
      open: true
    });
  }

  public closeHotspotDetail(hotspotID: string): void {
    this.hotspotDetailEnabledChange.next({
      hotspotID,
      open: false
    });
  }

  public toggleHotspots(enabled: boolean) {
    this.hotspotsEnabled = enabled;
    this.hotspotsEnabledChange.next(enabled);
  }

  public showHotspots() {
    this.toggleHotspots(true);
  }

  public hideHotspots() {
    this.toggleHotspots(false);
  }
}
